import { Env } from "@/env";
import { FileInput } from "@components/file-input";
import { ResponsiveModal } from "@components/responsive-modal";
import { authHeader } from "@features/utils";
import {
  Badge,
  Blockquote,
  Box,
  Button,
  Card,
  Heading,
  Link,
  Select,
  Spinner,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LorasType, LorasTypeDetails } from "@features/loras/type";
import { LoraEditForm } from "../lora-edit-form";

interface StepImportProps {
  isOpen?: boolean;
  onClose?: () => void;
  initialName?: string;
}

export const StepImport = ({
  isOpen = false,
  onClose,
  initialName = "",
}: StepImportProps) => {
  const { t } = useTranslation();

  // State management
  const [name, setName] = useState(initialName);
  const [customLora, setCustomLora] = useState<File | null>(null);
  const [customLoraPicture, setCustomLoraPicture] = useState<File | null>(null);
  const [customLoraLoading, setCustomLoraLoading] = useState(false);
  const [modalType, setModalType] = useState<"style" | "model">("model");
  const [modalSubtype, setModalSubtype] = useState<
    "avatar" | "animal" | "object" | "car"
  >("avatar");
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [customLoraDescription, setCustomLoraDescription] =
    useState<LorasTypeDetails>({} as any);

  const handleClose = () => {
    if (onClose) onClose();
    setCustomLora(null);
    setCustomLoraPicture(null);
    setShowAdvancedOptions(false);
  };

  const handleImportSubmit = async () => {
    if (!customLora || !name) return;

    setCustomLoraLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", customLora);
      if (customLoraPicture) {
        formData.append("picture", customLoraPicture);
      }
      formData.append("details", JSON.stringify(customLoraDescription));
      formData.append("type", modalType);
      formData.append("name", name);
      formData.append("subtype", modalSubtype);

      await fetch(Env.server + "/api/loras/import", {
        method: "POST",
        headers: {
          ...authHeader(),
        },
        body: formData,
      });

      toast.success(t("loras.type.importSuccessMessage"));
      document.location = "/";
      handleClose();
    } catch (e) {
      toast.error(t("loras.type.importError", "Erreur lors de l'importation"));
      console.error(e);
    } finally {
      setCustomLoraLoading(false);
    }
  };
  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        t("loras.type.import_next", {
          replace: {
            type:
              modalType === "model"
                ? t("loras.type.model")
                : t("loras.type.style"),
          },
        }) +
        (modalType === "model"
          ? ` (${t("loras.subtypes." + modalSubtype)})`
          : "")
      }
      height="auto"
      maxHeight="90vh"
    >
      {customLoraLoading && (
        <>
          <Spinner className="mx-auto my-4" />
          <Text size={"2"} className="text-center w-full block">
            {t("loras.type.uploading")}
          </Text>
        </>
      )}
      {!customLoraLoading && (
        <>
          <Text>{t("loras.type.uploadHelp")}</Text>
          <div className="my-4">
            <FileInput
              multiple={false}
              accept=".safetensors"
              value={customLora}
              onChange={(file) => setCustomLora(file)}
            />

            {/* Type Selector */}
            <Box className="mt-4 space-y-2">
              <Heading size="2">{t("loras.type.types.name", "Type")}</Heading>
              <Select.Root
                value={modalType}
                onValueChange={(value) => {
                  setModalType(value as "style" | "model");
                  if (value === "style") {
                    setModalSubtype("avatar"); // Reset subtype when switching to style
                  }
                }}
              >
                <Select.Trigger />
                <Select.Content>
                  <Select.Item value="model">
                    <span>{t("loras.type.model", "Modèle")}</span>
                  </Select.Item>
                  <Select.Item value="style">
                    <span>{t("loras.type.style", "Style")}</span>
                  </Select.Item>
                </Select.Content>
              </Select.Root>
            </Box>

            {/* Subtype Selector - Only show for models */}
            {modalType === "model" && (
              <Box className="mt-4 space-y-2">
                <Heading size="2">
                  {t("loras.type.types.subtype", "Sous-type")}
                </Heading>
                <Select.Root
                  value={modalSubtype}
                  onValueChange={(value) =>
                    setModalSubtype(
                      value as "avatar" | "animal" | "object" | "car"
                    )
                  }
                >
                  <Select.Trigger />
                  <Select.Content>
                    <Select.Item value="avatar">
                      <Badge
                        variant="solid"
                        color="blue"
                        size="1"
                        className="mr-2"
                      >
                        {t("loras.type.types.human.name")}
                      </Badge>
                    </Select.Item>
                    <Select.Item value="animal">
                      <Badge
                        variant="solid"
                        color="green"
                        size="1"
                        className="mr-2"
                      >
                        {t("loras.type.types.animal.name")}
                      </Badge>
                    </Select.Item>
                    <Select.Item value="object">
                      <Badge
                        variant="solid"
                        color="purple"
                        size="1"
                        className="mr-2"
                      >
                        {t("loras.type.types.object.name")}
                      </Badge>
                    </Select.Item>
                    <Select.Item value="car">
                      <Badge
                        variant="solid"
                        color="crimson"
                        size="1"
                        className="mr-2"
                      >
                        {t("loras.type.types.car.name")}
                      </Badge>
                    </Select.Item>
                  </Select.Content>
                </Select.Root>
              </Box>
            )}

            <Box className="mt-4 space-y-2">
              <Heading size="2">{t("loras.type.name")}</Heading>
              <TextField.Root
                placeholder={t("loras.type.name_import")}
                size="3"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Box>

            {modalType === "model" && (
              <Box className="mt-4 space-y-2">
                <Heading size="2">{t("loras.type.uploadSourceImage")}</Heading>
                <FileInput
                  multiple={false}
                  value={customLoraPicture}
                  onChange={(file) => setCustomLoraPicture(file)}
                />
                <Blockquote>
                  {t(
                    "loras.type.uploadSourceImageDesc",
                    "Cette image doit contenir un exemple d'image générée avec cette LoRA et nous permet d'afficher les réglages du modèle."
                  )}
                </Blockquote>
              </Box>
            )}

            {!showAdvancedOptions && modalType === "model" && (
              <Link
                className="block mt-4"
                href="#"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              >
                {t("loras.type.showAdvancedOptions", "Options avancées")}
              </Link>
            )}

            {showAdvancedOptions && modalType === "model" && (
              <>
                <Link
                  className="block mt-4"
                  href="#"
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                >
                  {t(
                    "loras.type.hideAdvancedOptions",
                    "Cacher les options avancées"
                  )}
                </Link>
                <Card className="pt-0 mt-4">
                  <LoraEditForm
                    lora={{
                      status: "completed",
                      name,
                      type: modalType,
                      subtype: modalSubtype,
                      ai: {} as any,
                    }}
                    draft={{ details: customLoraDescription } as LorasType}
                    setDraft={(e) => setCustomLoraDescription(e.details)}
                  />
                </Card>
              </>
            )}

            <Button
              size="2"
              color="gray"
              variant="outline"
              className="mt-4 float-left"
              onClick={handleClose}
            >
              {t("loras.type.cancel")}
            </Button>

            <Button
              size="2"
              disabled={
                !customLora ||
                !name ||
                (!customLoraPicture && modalType === "model")
              }
              className="mt-4 float-right"
              onClick={handleImportSubmit}
            >
              {t("loras.type.import_next", {
                replace: {
                  type:
                    modalType === "model"
                      ? t("loras.type.model")
                      : t("loras.type.style"),
                },
              })}
            </Button>
          </div>
        </>
      )}
    </ResponsiveModal>
  );
};
